import { zipSync, strToU8 } from 'fflate';
import { csvEscape, formatMoneyCents } from './format';
import { formatPeriodLabel } from './periods';
import { formatClinicDate, formatClinicDateTime } from './timezone';
import { formatCashierBucketLabel } from './cashierEngine';
import type { CashierPaymentRow, CashierReportResult } from './cashierTypes';
import { isKnownPaymentMethod, PAYMENT_METHOD_LABEL_KEYS } from './paymentMethods';
import {
  CARD_TINTS,
  COLORS,
  CONTENT_WIDTH,
  MARGIN,
  PdfDocument,
  drawReportHeader,
  measureText,
  pdfSafeText,
} from './pdfKit';

export interface CashierExportLabels {
  title: string;
  subtitle: string;
  period: string;
  generated: string;
  clinic: string;
  brand: string;
  keyFigures: string;
  totalReceived: string;
  refunds: string;
  netReceived: string;
  outstanding: string;
  outstandingHint: string;
  outstandingInvoices: string;
  outstandingEmpty: string;
  outstandingTotal: string;
  issued: string;
  due: string;
  invoiceTotal: string;
  invoicePaid: string;
  invoiceStatusLabels: Record<string, string>;
  transactions: string;
  paymentMethods: string;
  method: string;
  amount: string;
  count: string;
  total: string;
  dailySummary: string;
  date: string;
  received: string;
  net: string;
  refundSection: string;
  refundCount: string;
  cashReceived: string;
  cashTransactions: string;
  clinioPay: string;
  feesUnavailable: string;
  payoutUnavailable: string;
  transactionDetail: string;
  dateTime: string;
  patient: string;
  invoice: string;
  channel: string;
  reference: string;
  status: string;
  practitioner: string;
  clinicColumn: string;
  channelClinioPay: string;
  channelOther: string;
  statusReceived: string;
  statusRefunded: string;
  truncated: string;
  footerReport: string;
  page: string;
  pageOf: string;
  methodLabels: Record<string, string>;
}

function methodLabel(key: string, labels: CashierExportLabels): string {
  if (labels.methodLabels[key]) return labels.methodLabels[key];
  if (isKnownPaymentMethod(key)) return labels.methodLabels[PAYMENT_METHOD_LABEL_KEYS[key]] ?? key;
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Plain number for CSV/Excel. Danish uses a decimal comma and no "kr." suffix. */
export function exportAmount(cents: number, locale: string): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const kroner = Math.floor(abs / 100);
  const ore = String(abs % 100).padStart(2, '0');
  const danish = locale.toLowerCase().startsWith('da');
  const grouped = danish
    ? String(kroner).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    : String(kroner);
  return `${negative ? '-' : ''}${grouped}${danish ? ',' : '.'}${ore}`;
}

function csvSeparator(locale: string): string {
  return locale.toLowerCase().startsWith('da') ? ';' : ',';
}

function money(cents: number, currency: string, locale: string): string {
  return formatMoneyCents(cents, currency, locale);
}

function csvMoney(cents: number, locale: string): string {
  return exportAmount(cents, locale);
}

function channelLabel(channel: string, labels: CashierExportLabels): string {
  return channel === 'clinio_pay' ? labels.channelClinioPay : labels.channelOther;
}

function statusLabel(status: string, labels: CashierExportLabels): string {
  return status === 'refunded' ? labels.statusRefunded : labels.statusReceived;
}

function invoiceStatusLabel(status: string, labels: CashierExportLabels): string {
  return labels.invoiceStatusLabels[status] ?? status;
}

function transactionNet(row: CashierPaymentRow): number {
  return row.kind === 'refund' ? -Math.abs(row.amount_cents) : row.amount_cents;
}

function transactionGross(row: CashierPaymentRow): number {
  return row.kind === 'refund' ? 0 : row.amount_cents;
}

function transactionRefund(row: CashierPaymentRow): number {
  return row.kind === 'refund' ? Math.abs(row.amount_cents) : 0;
}

export function cashierReportToCsv(
  result: CashierReportResult,
  labels: CashierExportLabels,
  locale = 'da-DK',
): string {
  const lines: string[] = [];
  const separator = csvSeparator(locale);
  const push = (values: string[]) =>
    lines.push(values.map((value) => csvEscape(value, separator)).join(separator));
  const amount = (cents: number) => csvMoney(cents, locale);

  push([labels.title]);
  push([labels.period, formatPeriodLabel(result.period, locale)]);
  if (result.clinic_name) push([labels.clinic, result.clinic_name]);
  push([]);
  push([labels.keyFigures]);
  push([labels.totalReceived, amount(result.summary.total_received_cents)]);
  push([labels.refunds, amount(result.summary.refunds_cents)]);
  push([labels.netReceived, amount(result.summary.net_received_cents)]);
  push([labels.outstanding, amount(result.summary.outstanding_cents)]);
  push([labels.transactions, String(result.summary.transaction_count)]);
  push([]);
  push([labels.paymentMethods, labels.received, labels.refunds, labels.net, labels.transactions]);
  for (const method of result.methods) {
    push([
      methodLabel(method.method_key, labels),
      amount(method.received_cents),
      amount(method.refund_cents),
      amount(method.net_cents),
      String(method.transaction_count),
    ]);
  }
  push([
    labels.total,
    amount(result.summary.total_received_cents),
    amount(result.summary.refunds_cents),
    amount(result.summary.net_received_cents),
    String(result.summary.transaction_count),
  ]);
  push([]);
  push([labels.outstandingInvoices]);
  if (result.outstandingInvoices.length === 0) {
    push([labels.outstandingEmpty]);
  } else {
    push([
      labels.invoice,
      labels.patient,
      labels.issued,
      labels.due,
      labels.invoiceTotal,
      labels.invoicePaid,
      labels.outstanding,
      labels.status,
    ]);
    for (const invoice of result.outstandingInvoices) {
      push([
        invoice.invoice_number ?? '',
        invoice.patient_name ?? '',
        formatClinicDate(invoice.issued_at, result.timezone),
        formatClinicDate(invoice.due_at, result.timezone),
        amount(invoice.total_cents),
        amount(invoice.paid_cents),
        amount(invoice.outstanding_cents),
        invoiceStatusLabel(invoice.status, labels),
      ]);
    }
    push([
      labels.outstandingTotal,
      '',
      '',
      '',
      '',
      '',
      amount(result.summary.outstanding_cents),
      '',
    ]);
  }
  push([]);
  push([
    labels.dateTime,
    labels.patient,
    labels.invoice,
    labels.reference,
    labels.method,
    labels.channel,
    labels.received,
    labels.refunds,
    labels.net,
    labels.status,
    labels.practitioner,
    labels.clinicColumn,
  ]);
  for (const row of result.transactions) {
    push([
      formatClinicDateTime(row.recorded_at || row.payment_date, result.timezone),
      row.patient_name ?? '',
      row.invoice_number ?? '',
      row.payment_reference ?? '',
      methodLabel(row.payment_method_key, labels),
      channelLabel(row.channel, labels),
      amount(transactionGross(row)),
      amount(transactionRefund(row)),
      amount(transactionNet(row)),
      statusLabel(row.status, labels),
      row.practitioner_name ?? '',
      row.clinic_name ?? '',
    ]);
  }
  return `\uFEFF${lines.join('\n')}`;
}

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function parseCsvLine(line: string, separator: string): string[] {
  const values: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else current += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === separator) {
      values.push(current);
      current = '';
    } else current += ch;
  }
  values.push(current);
  return values;
}

function parseExportNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^-?\d{1,3}(?:\.\d{3})*,\d{2}$/.test(trimmed) && !/^-?\d+\.\d{2}$/.test(trimmed)) {
    return null;
  }
  const normalized = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Worksheet XML. Amount cells are numbers, not "937,50 kr." text. */
export function cashierReportToExcelXml(
  result: CashierReportResult,
  labels: CashierExportLabels,
  locale = 'da-DK',
): string {
  const separator = csvSeparator(locale);
  const csv = cashierReportToCsv(result, labels, locale).replace(/^\uFEFF/, '');
  const rows = csv.split('\n').map((line, rowIndex) => {
    const values = parseCsvLine(line, separator);
    const cells = values.map((value, columnIndex) => {
      const ref = `${columnName(columnIndex)}${rowIndex + 1}`;
      const amount = parseExportNumber(value);
      if (amount != null) {
        return `<c r="${ref}"><v>${amount}</v></c>`;
      }
      return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(value)}</t></is></c>`;
    });
    return `<row r="${rowIndex + 1}">${cells.join('')}</row>`;
  });
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rows.join('')}</sheetData></worksheet>`;
}

function columnName(index: number): string {
  let n = index + 1;
  let name = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

/** Real .xlsx (Office Open XML), stored uncompressed so the sheet XML stays readable. */
export function cashierReportToXlsx(
  result: CashierReportResult,
  labels: CashierExportLabels,
  locale = 'da-DK',
): Uint8Array {
  const sheet = cashierReportToExcelXml(result, labels, locale);
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;
  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${xmlEscape(labels.title.slice(0, 31) || 'Kasse')}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;
  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;
  return zipSync(
    {
      '[Content_Types].xml': strToU8(contentTypes),
      '_rels/.rels': strToU8(rels),
      'xl/workbook.xml': strToU8(workbook),
      'xl/_rels/workbook.xml.rels': strToU8(workbookRels),
      'xl/worksheets/sheet1.xml': strToU8(sheet),
    },
    { level: 0 },
  );
}

function fitValueSize(value: string, maxWidth: number, preferred: number): number {
  let size = preferred;
  while (size > 8 && measureText(value, size, true) > maxWidth) size -= 0.5;
  return size;
}

function drawSectionTitle(doc: PdfDocument, title: string) {
  doc.ensureSpace(28);
  doc.text(title.toUpperCase(), MARGIN, doc.y - 1, {
    size: 8,
    bold: true,
    color: COLORS.lavenderAccent,
  });
  doc.gap(5);
  doc.line(MARGIN, doc.y, MARGIN + 22, doc.y, COLORS.accent, 1.1);
  doc.gap(11);
}

function drawTable(
  doc: PdfDocument,
  headers: string[],
  rows: string[][],
  widths: number[],
) {
  const rowH = 16;
  const headerH = 18;
  doc.ensureSpace(headerH + rowH);
  const headerBottom = doc.y - headerH;
  doc.fillRoundedRect(MARGIN, headerBottom, CONTENT_WIDTH, headerH, 4, COLORS.headerBg);
  let x = MARGIN + 8;
  headers.forEach((header, index) => {
    doc.text(header, x, doc.y - 12, {
      size: 7,
      bold: true,
      color: COLORS.muted,
      maxWidth: widths[index],
      align: index === 0 ? 'left' : 'right',
    });
    x += widths[index];
  });
  doc.y -= headerH;
  rows.forEach((cells, rowIndex) => {
    doc.ensureSpace(rowH);
    if (rowIndex % 2 === 1) {
      doc.fillRect(MARGIN, doc.y - rowH + 2, CONTENT_WIDTH, rowH, COLORS.altRow);
    }
    let cellX = MARGIN + 8;
    cells.forEach((value, index) => {
      doc.text(pdfSafeText(value), cellX, doc.y - 11, {
        size: 8,
        bold: rowIndex === rows.length - 1,
        maxWidth: widths[index] - 4,
        align: index === 0 ? 'left' : 'right',
      });
      cellX += widths[index];
    });
    doc.y -= rowH;
  });
  doc.gap(12);
}

export function cashierReportToPdf(
  result: CashierReportResult,
  labels: CashierExportLabels,
  options: { includeDetails?: boolean; generatedAt?: Date; locale?: string } = {},
): Uint8Array {
  const locale = options.locale ?? 'da-DK';
  const generatedAt = options.generatedAt ?? new Date();
  const currency = result.currency;
  const period = formatPeriodLabel(result.period, locale);
  const doc = new PdfDocument();
  doc.footerLeft = `${labels.brand} · ${labels.footerReport} · ${period}`;
  doc.footerRightPrefix = labels.page;
  doc.footerOf = labels.pageOf;

  const meta = [
    ...(result.clinic_name ? [{ label: labels.clinic, value: result.clinic_name }] : []),
    { label: labels.period, value: period },
    {
      label: labels.generated,
      value: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(generatedAt),
    },
  ];
  drawReportHeader(doc, {
    brand: labels.brand,
    title: labels.title,
    subtitle: labels.subtitle,
    meta,
  });

  drawSectionTitle(doc, labels.keyFigures);
  const figures = [
    { label: labels.totalReceived, value: money(result.summary.total_received_cents, currency, locale) },
    { label: labels.refunds, value: money(result.summary.refunds_cents, currency, locale) },
    { label: labels.netReceived, value: money(result.summary.net_received_cents, currency, locale) },
    { label: labels.outstanding, value: money(result.summary.outstanding_cents, currency, locale) },
    { label: labels.transactions, value: String(result.summary.transaction_count) },
  ];
  const gap = 7;
  const figureRows = [figures.slice(0, 3), figures.slice(3)];
  figureRows.forEach((row) => {
    const cardW = (CONTENT_WIDTH - gap * (row.length - 1)) / row.length;
    const cardH = 48;
    doc.ensureSpace(cardH + 6);
    row.forEach((figure, index) => {
      const x = MARGIN + index * (cardW + gap);
      const tint = CARD_TINTS[index % CARD_TINTS.length];
      doc.fillRoundedRect(x, doc.y - cardH, cardW, cardH, 7, tint.bg);
      doc.strokeRoundedRect(x, doc.y - cardH, cardW, cardH, 7, COLORS.line, 0.45);
      doc.fillRoundedRect(x + 8, doc.y - cardH + 10, 2.2, cardH - 20, 1, tint.accent);
      doc.text(figure.label, x + 14, doc.y - 13, { size: 7, color: COLORS.muted, maxWidth: cardW - 20 });
      const valueSize = fitValueSize(figure.value, cardW - 20, 12);
      doc.text(figure.value, x + 14, doc.y - 32, { size: valueSize, bold: true, maxWidth: cardW - 20 });
    });
    doc.y -= cardH;
    doc.gap(8);
  });
  doc.gap(6);

  if (result.methods.length > 0) {
    drawSectionTitle(doc, labels.paymentMethods);
    const widths = [160, 95, 90, 90, 88];
    drawTable(
      doc,
      [labels.method, labels.received, labels.refunds, labels.net, labels.transactions],
      [
        ...result.methods.map((method) => [
          methodLabel(method.method_key, labels),
          money(method.received_cents, currency, locale),
          money(method.refund_cents, currency, locale),
          money(method.net_cents, currency, locale),
          String(method.transaction_count),
        ]),
        [
          labels.total,
          money(result.summary.total_received_cents, currency, locale),
          money(result.summary.refunds_cents, currency, locale),
          money(result.summary.net_received_cents, currency, locale),
          String(result.summary.transaction_count),
        ],
      ],
      widths,
    );
  }

  drawSectionTitle(doc, labels.outstandingInvoices);
  if (result.outstandingInvoices.length === 0) {
    doc.text(labels.outstandingEmpty, MARGIN, doc.y - 2, { size: 8, color: COLORS.muted, maxWidth: CONTENT_WIDTH });
    doc.gap(16);
  } else {
    drawTable(
      doc,
      [
        labels.invoice,
        labels.patient,
        labels.issued,
        labels.due,
        labels.invoiceTotal,
        labels.invoicePaid,
        labels.outstanding,
      ],
      [
        ...result.outstandingInvoices.map((invoice) => [
          invoice.invoice_number ?? '—',
          invoice.patient_name ?? '—',
          formatClinicDate(invoice.issued_at, result.timezone),
          formatClinicDate(invoice.due_at, result.timezone),
          money(invoice.total_cents, currency, locale),
          money(invoice.paid_cents, currency, locale),
          money(invoice.outstanding_cents, currency, locale),
        ]),
        [
          labels.outstandingTotal,
          '',
          '',
          '',
          '',
          '',
          money(result.summary.outstanding_cents, currency, locale),
        ],
      ],
      [78, 90, 70, 70, 75, 70, 70],
    );
  }

  if (result.periodRows.length > 0) {
    drawSectionTitle(doc, labels.dailySummary);
    drawTable(
      doc,
      [labels.date, labels.received, labels.refunds, labels.net, labels.transactions],
      [
        ...result.periodRows.map((row) => [
          formatCashierBucketLabel(row.bucket, result.grain, locale),
          money(row.received_cents, currency, locale),
          money(row.refund_cents, currency, locale),
          money(row.net_cents, currency, locale),
          String(row.transaction_count),
        ]),
        [
          labels.total,
          money(result.summary.total_received_cents, currency, locale),
          money(result.summary.refunds_cents, currency, locale),
          money(result.summary.net_received_cents, currency, locale),
          String(result.summary.transaction_count),
        ],
      ],
      [140, 110, 100, 100, 73],
    );
  }

  if (result.refunds.length > 0) {
    drawSectionTitle(doc, labels.refundSection);
    drawTable(
      doc,
      [labels.method, labels.refunds, labels.refundCount],
      [
        ...result.refunds.map((method) => [
          methodLabel(method.method_key, labels),
          money(method.refund_cents, currency, locale),
          String(method.refund_count),
        ]),
        [labels.total, money(result.summary.refunds_cents, currency, locale), String(result.summary.refund_count)],
      ],
      [250, 160, 113],
    );
  }

  const includeDetails =
    options.includeDetails === true ||
    (options.includeDetails !== false && result.transactions.length > 0 && result.transactions.length <= 80);

  if (includeDetails && result.transactions.length > 0) {
    drawSectionTitle(doc, labels.transactionDetail);
    if (result.transactionsTruncated) {
      doc.text(labels.truncated, MARGIN, doc.y - 2, { size: 8, color: COLORS.muted, maxWidth: CONTENT_WIDTH });
      doc.gap(12);
    }
    const widths = [90, 110, 80, 90, 80, 73];
    drawTable(
      doc,
      [labels.dateTime, labels.patient, labels.invoice, labels.method, labels.amount, labels.status],
      result.transactions.slice(0, 80).map((txn) => [
        formatClinicDateTime(txn.recorded_at || txn.payment_date, result.timezone),
        txn.patient_name ?? '—',
        txn.invoice_number ?? '—',
        methodLabel(txn.payment_method_key, labels),
        money(transactionNet(txn), currency, locale),
        statusLabel(txn.status, labels),
      ]),
      widths,
    );
  }

  return doc.build();
}
